import {
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  RingGeometry,
  Vector3,
} from 'three';
import { clamp } from '../util/math.js';
import { chooseEnemyType } from './enemies.js';

const ringGeometry = new RingGeometry(0.92, 1, 48);
const miniBosses = ['charger', 'summoner', 'sniper', 'juggernaut'];

let bossState = null;

export function initBossDirector(world) {
  bossState = {
    world,
    activeBoss: null,
    arenaRings: [],
  };
  return bossState;
}

export function isBossWave(wave) {
  return wave > 0 && wave % 5 === 0;
}

export function createBossWaveQueue(wave, mapTier = 0) {
  const bossWave = isBossWave(wave);
  const base = bossWave
    ? 16 + Math.floor(wave * 2.2)
    : 16 + wave * 5 + Math.floor(mapTier * 3);
  const queue = [];

  if (bossWave) {
    const variant = miniBosses[Math.floor(wave / 5 - 1) % miniBosses.length];
    queue.push({ boss: true, type: 'boss', variant });
    if (variant === 'summoner') {
      for (let i = 0; i < 8; i += 1) queue.push({ type: 'swarm' });
    }
    for (let i = 0; i < base; i += 1) {
      queue.push({ type: chooseEnemyType(wave, i) });
    }
  } else {
    for (let i = 0; i < base; i += 1) {
      queue.push({ type: chooseEnemyType(wave, i) });
    }
  }

  return queue;
}

export function startBossWave(game, options = {}) {
  if (!bossState) throw new Error('[boss] initBossDirector() must run before startBossWave().');

  const {
    spawnEnemy,
    player,
    onIntro = null,
  } = options;
  game.boss = null;
  game.spawnQueue = createBossWaveQueue(game.wave, game.mapTier || 0);
  spawnWaveRing(0xff3156, { ttl: 0.95, maxScale: 12.5 });
  onIntro?.(`The Warden - Rogue MK-${game.wave}`);

  if (spawnEnemy) {
    const spec = game.spawnQueue.shift();
    const boss = spawnEnemy(spec, { player, game });
    bossState.activeBoss = boss;
    game.boss = boss;
    return boss;
  }

  return null;
}

export function updateBossDirector(dt, options = {}) {
  if (!bossState) return;

  const {
    player = null,
    onArenaDamage = null,
  } = options;

  updateArenaRings(dt, player, onArenaDamage);
  if (bossState.activeBoss && !bossState.activeBoss.alive) {
    bossState.activeBoss = null;
  }
}

export function bossSlam(enemy, options = {}) {
  if (!bossState || !enemy?.alive) return null;

  const ring = spawnWaveRing(0xff3156, {
    origin: enemy.pos.clone(),
    ttl: options.ttl ?? 0.95,
    maxScale: options.radius ?? 12.5,
    damage: options.damage ?? 18,
  });
  ring.mesh.position.set(enemy.pos.x, 0.09, enemy.pos.z);
  return ring;
}

export function spawnWaveRing(color = 0x53ffb5, options = {}) {
  if (!bossState) throw new Error('[boss] initBossDirector() must run before spawnWaveRing().');

  const mesh = new Mesh(
    ringGeometry,
    new MeshBasicMaterial({
      color,
      transparent: true,
      opacity: options.opacity ?? 0.78,
      depthWrite: false,
      side: DoubleSide,
    }),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(0, 0.09, 0);
  bossState.world.ringRoot.add(mesh);

  const ring = {
    mesh,
    age: 0,
    ttl: options.ttl ?? 0.65,
    maxScale: options.maxScale ?? 6,
    origin: options.origin?.clone?.() || new Vector3(),
    damage: options.damage || 0,
    hit: false,
  };
  bossState.arenaRings.push(ring);
  return ring;
}

export function getBossHealthPercent(game) {
  const boss = game?.boss || bossState?.activeBoss;
  if (!boss?.alive) return 0;
  return clamp(boss.hp / boss.maxHp, 0, 1);
}

export function clearBossArena() {
  if (!bossState) return;
  for (const ring of bossState.arenaRings) {
    bossState.world.ringRoot.remove(ring.mesh);
  }
  bossState.arenaRings.length = 0;
  bossState.activeBoss = null;
}

function updateArenaRings(dt, player, onArenaDamage) {
  for (const ring of bossState.arenaRings) {
    ring.age += dt;
    const t = clamp(ring.age / ring.ttl, 0, 1);
    const scale = ring.maxScale * t;
    ring.mesh.scale.setScalar(scale);
    ring.mesh.material.opacity = (1 - t) * 0.78;

    if (!ring.hit && ring.damage > 0 && player) {
      const dist = Math.hypot(player.pos.x - ring.origin.x, player.pos.z - ring.origin.z);
      if (dist < scale && dist > scale - 1.6) {
        ring.hit = true;
        onArenaDamage?.(ring.damage, ring.origin);
      }
    }
  }

  for (let i = bossState.arenaRings.length - 1; i >= 0; i -= 1) {
    if (bossState.arenaRings[i].age <= bossState.arenaRings[i].ttl) continue;
    bossState.world.ringRoot.remove(bossState.arenaRings[i].mesh);
    bossState.arenaRings.splice(i, 1);
  }
}
